import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/stocks')
const BOOKS_BY_TYPE_API_URL = apiUrl('/books/type')

export const STOCK_PAGE_SIZE = 20

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Stock request failed (${response.status})`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  return response.status === 204 ? null : response.json()
}

function findList(data, keys) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of keys) {
    const list = findList(data[key], keys)
    if (list.length || Array.isArray(data[key])) return list
  }

  return []
}

function findBooks(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of [
    'books',
    'bookList',
    'booklist',
    'publishedBooks',
    'published_books',
    'results',
    'items',
    'rows',
    'result',
    'payload',
    'data',
  ]) {
    const books = findBooks(data[key])
    if (books.length) return books
  }

  for (const value of Object.values(data)) {
    const books = findBooks(value)
    if (books.length && books.every((book) => book && typeof book === 'object'))
      return books
  }

  return []
}

export async function getStockMovements({
  page = 1,
  search = '',
  direction = '',
  signal,
} = {}) {
  const loadPage = async (pageNumber) => {
    const params = new URLSearchParams({
      limit: String(STOCK_PAGE_SIZE),
      page: String(pageNumber),
      order: 'date_desc',
    })
    if (search.trim()) params.set('search', search.trim())
    if (direction) params.set('direction', direction.toUpperCase())

    const data = await request(`${API_BASE_URL}/all?${params}`, { signal })
    return {
      movements: findList(data, [
        'stocks',
        'stockMovements',
        'movements',
        'results',
        'items',
        'rows',
        'result',
        'payload',
        'data',
      ]),
      hasNext: Boolean(
        data?.hasNextPage ??
        data?.has_next_page ??
        data?.hasNext ??
        data?.has_next ??
        data?.data?.hasNextPage ??
        data?.data?.has_next_page ??
        false,
      ),
    }
  }

  const result = await loadPage(page)

  return {
    movements: result.movements,
    nextCursor: null,
    nextPage: result.hasNext ? page + 1 : null,
    currentPage: page,
  }
}

export async function getStockBooks(bookType, signal) {
  if (!bookType) throw new Error('Book type is required.')

  const data = await request(
    `${BOOKS_BY_TYPE_API_URL}/${encodeURIComponent(bookType)}`,
    { signal },
  )
  return findBooks(data)
}

export async function createStockMovement(payload) {
  const data = await request(`${API_BASE_URL}/add`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return data?.data ?? data
}

export async function updateStockMovement(id, payload) {
  const data = await request(
    `${API_BASE_URL}/updateStock/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(payload) },
  )
  return data?.data ?? data
}

export async function deleteStockMovement(id) {
  return request(`${API_BASE_URL}/deleteStock/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}
